import { Component, Input, inject } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { translate } from '@jsverse/transloco';
import { UntilDestroy } from '@ngneat/until-destroy';
import { MessageService } from 'primeng/api';
import { QuickFilterItem, QuickFiltersService } from '../service/quick-filters.service';

interface FilterHeaders {
  active: boolean;
  limit: number;
}
@UntilDestroy()
@Component({
  selector: 'rmb-quick-filters-bar',
  templateUrl: './quick-filters-bar.component.html',
  styleUrls: ['./quick-filters-bar.component.scss'],
  standalone: false,
})
export class QuickFiltersBarComponent {
  private messageService = inject(MessageService);
  private quickFiltersService = inject(QuickFiltersService);

  @Input() filterForm: FormGroup;
  @Input() TRANSLOCO_PREFIX: string;
  @Input() context: string;
  @Input() filtersList = [];
  @Input() editFilter: QuickFilterItem;
  @Input() store: any;
  @Input() maxLength: number;
  invalidForm: boolean;

  get active() {
    return this.filterForm?.get('active')?.value;
  }

  get limit() {
    return this.filterForm?.get('limit')?.value;
  }

  onReset() {
    this.filterForm.reset();
    this.invalidForm = false;
    this.editFilter = null;
  }

  async addOrUpdateFilter(filters: FilterHeaders) {
    this.filterForm.patchValue({ active: filters.active, limit: filters.limit });
    this.atLeastOneValidator();
    this.filterForm.markAllAsTouched();

    this.checkIfFilterNameExist();

    if (this.filterForm.invalid || this.showWarning()) {
      return;
    }
    if (this.editFilter) {
      const index = this.filtersList.findIndex((item) => item.customId === this.filterForm.value.customId);
      const itemsCopy = [...this.filtersList];
      itemsCopy[index] = { ...this.filterForm.value };
      this.filtersList = itemsCopy;
      const data = this.quickFiltersService.mapObjectToJSON(this.filtersList, this.context);
      this.quickFiltersService.onUpdate(this.filterForm.value.customId, data, this.store);
      this.filterForm.reset();
      this.editFilter = null;
    } else {
      const customID = this.filterForm.value.name.toString().substring(0, 3) + '_' + Math.floor(Math.random() * Date.now()).toString(32);
      this.filterForm.value.customId = customID.toLowerCase();
      const valueToSend = [];
      valueToSend.push(...this.filtersList, this.filterForm.value);
      const data = this.quickFiltersService.mapObjectToJSON(valueToSend, this.context);
      this.quickFiltersService.onSave(data, this.filterForm.value, this.store);
      this.filterForm.reset();
    }
  }

  showWarning(): boolean {
    const isMax = this.quickFiltersService.maxActiveFiltersCheck(
      this.filtersList,
      this.filterForm.value.active,
      this.maxLength,
      this.editFilter
    );

    if (isMax) {
      this.messageService.add({
        severity: 'warn',
        summary: translate(this.TRANSLOCO_PREFIX + '.toast.title'),
        detail: translate(this.TRANSLOCO_PREFIX + '.toast.message'),
        life: 30000,
      });
      return true;
    }

    return false;
  }

  atLeastOneValidator(): boolean {
    if (this.filterForm.errors?.anyOfInvalid) {
      this.invalidForm = true;
    } else {
      this.invalidForm = false;
    }
    return this.invalidForm;
  }

  checkIfFilterNameExist() {
    if (this.filtersList?.length) {
      if (
        this.filtersList.some(
          (item) =>
            item.name.toLowerCase() === this.filterForm.value.name?.toLowerCase() && item.customId !== this.filterForm.value.customId
        )
      ) {
        this.filterForm.controls['name'].setErrors({ invalid: true });
      }
    }
  }
}
